import { supabase } from '../lib/supabaseClient';
import type {
  Bloque,
  Curso,
  Espacio,
  Inscripcion,
  Intento,
  Leccion,
  Opcion,
  Pregunta,
  Premio,
  ResumenCurso,
  TipoBloque,
} from '../domain/types';
import type {
  AcademiaRepo,
  RespuestaElegida,
  ResultadoIntento,
  RevisionRespuesta,
} from './AcademiaRepo';

/**
 * Cliente apuntado al esquema `academia`.
 *
 * ⚠️ PostgREST solo expone `public` por defecto. `academia` tiene que estar declarado en
 * Settings → API → Exposed schemas del proyecto de Supabase, o TODA consulta de este
 * archivo falla con `PGRST106: Invalid schema`. No es algo que se arregle desde el código.
 */
const db = () => supabase.schema('academia');

/**
 * Implementación de fase 1: el contenido y el progreso viven en el esquema `academia`.
 *
 * Cumple exactamente la misma interfaz que `LocalRepo`, así que ninguna pantalla cambió al
 * pasar de una a otra. Ese era el punto de que `AcademiaRepo` existiera.
 *
 * Dos cosas que NO hace, a propósito:
 *
 *  1. **No lee `es_correcta`.** La 0065 le quitó ese permiso a `authenticated`: pedirla
 *     haría fallar la consulta entera con 42501. Por eso `Opcion.esCorrecta` es opcional
 *     en el tipo — acá simplemente no viene.
 *
 *  2. **No calcula la nota.** La calcula `academia.calificar_intento` en el servidor. Si el
 *     navegador pudiera calcularla, tampoco valdría como evidencia de capacitación.
 */
export class SupabaseRepo implements AcademiaRepo {
  private cacheCursos: Map<string, Curso> = new Map();

  persisteEnServidor(): boolean {
    return true;
  }

  async obtenerEspacio(slug: string): Promise<Espacio | null> {
    const { data, error } = await db()
      .from('espacios')
      .select('id, slug, nombre, cliente, tema, logo_path, activo')
      .eq('slug', slug)
      .eq('activo', true)
      .maybeSingle();

    if (error || !data) return null;
    return {
      id: data.id,
      slug: data.slug,
      nombre: data.nombre,
      cliente: data.cliente,
      tema: data.tema,
      logoUrl: data.logo_path ?? '',
      activo: data.activo,
    };
  }

  /**
   * Trae el curso entero en dos consultas y no en N+1.
   *
   * Un curso son ~6 lecciones, ~12 bloques, 5 preguntas y 20 alternativas. Pedirlo pieza
   * por pieza sería una decena de viajes por curso desde un celular en 4G; con los embeds
   * de PostgREST son dos.
   */
  private async cargarCursos(espacioId: string): Promise<Curso[]> {
    const { data, error } = await db()
      .from('cursos')
      .select(
        `id, espacio_id, slug, titulo, descripcion, emoji, etiqueta,
         gradiente_1, gradiente_2, duracion_min, orden, estado, nota_minima,
         insignia_ico, insignia_nombre, lecciones_previstas, preguntas_previstas,
         lecciones ( id, curso_id, titulo, orden,
                     bloques ( id, leccion_id, orden, tipo, contenido ) ),
         preguntas ( id, curso_id, enunciado, explicacion, orden,
                     opciones ( id, pregunta_id, texto, orden ) )`,
      )
      .eq('espacio_id', espacioId)
      .in('estado', ['publicado', 'proximamente'])
      .order('orden');

    if (error) throw new Error(`No se pudieron cargar los cursos: ${error.message}`);

    const cursos = (data ?? []).map((fila) => this.aCurso(fila));
    for (const curso of cursos) this.cacheCursos.set(curso.id, curso);
    return cursos;
  }

  private aCurso(f: Record<string, any>): Curso {
    const lecciones: Leccion[] = (f.lecciones ?? [])
      .map(
        (l: Record<string, any>): Leccion => ({
          id: l.id,
          cursoId: l.curso_id,
          titulo: l.titulo,
          orden: l.orden,
          bloques: (l.bloques ?? [])
            .map(
              (b: Record<string, any>): Bloque => ({
                id: b.id,
                leccionId: b.leccion_id,
                orden: b.orden,
                tipo: b.tipo as TipoBloque,
                contenido: b.contenido ?? {},
              }),
            )
            .sort((a: Bloque, b: Bloque) => a.orden - b.orden),
        }),
      )
      .sort((a: Leccion, b: Leccion) => a.orden - b.orden);

    const preguntas: Pregunta[] = (f.preguntas ?? [])
      .map(
        (p: Record<string, any>): Pregunta => ({
          id: p.id,
          cursoId: p.curso_id,
          enunciado: p.enunciado,
          explicacion: p.explicacion,
          orden: p.orden,
          // Sin `esCorrecta`: la columna no viaja al navegador (0065).
          opciones: (p.opciones ?? [])
            .map(
              (o: Record<string, any>): Opcion => ({
                id: o.id,
                preguntaId: o.pregunta_id,
                texto: o.texto,
                orden: o.orden,
              }),
            )
            .sort((a: Opcion, b: Opcion) => a.orden - b.orden),
        }),
      )
      .sort((a: Pregunta, b: Pregunta) => a.orden - b.orden);

    return {
      id: f.id,
      espacioId: f.espacio_id,
      slug: f.slug,
      titulo: f.titulo,
      descripcion: f.descripcion,
      emoji: f.emoji,
      etiqueta: f.etiqueta,
      gradiente1: f.gradiente_1,
      gradiente2: f.gradiente_2,
      duracionMin: f.duracion_min,
      orden: f.orden,
      estado: f.estado,
      notaMinima: f.nota_minima,
      insigniaIco: f.insignia_ico,
      insigniaNombre: f.insignia_nombre,
      leccionesPrevistas: f.lecciones_previstas ?? undefined,
      preguntasPrevistas: f.preguntas_previstas ?? undefined,
      lecciones,
      preguntas,
    };
  }

  async listarCursos(espacioId: string): Promise<Curso[]> {
    return this.cargarCursos(espacioId);
  }

  async obtenerCurso(cursoId: string): Promise<Curso | null> {
    const enCache = this.cacheCursos.get(cursoId);
    if (enCache) return enCache;

    const { data, error } = await db()
      .from('cursos')
      .select(
        `id, espacio_id, slug, titulo, descripcion, emoji, etiqueta,
         gradiente_1, gradiente_2, duracion_min, orden, estado, nota_minima,
         insignia_ico, insignia_nombre, lecciones_previstas, preguntas_previstas,
         lecciones ( id, curso_id, titulo, orden,
                     bloques ( id, leccion_id, orden, tipo, contenido ) ),
         preguntas ( id, curso_id, enunciado, explicacion, orden,
                     opciones ( id, pregunta_id, texto, orden ) )`,
      )
      .eq('id', cursoId)
      .maybeSingle();

    if (error || !data) return null;
    const curso = this.aCurso(data);
    this.cacheCursos.set(curso.id, curso);
    return curso;
  }

  async obtenerInscripcion(cursoId: string): Promise<Inscripcion | null> {
    const { data, error } = await db()
      .from('inscripciones')
      .select('id, curso_id, estado, iniciado_at, completado_at, avance_bloques ( bloque_id )')
      .eq('curso_id', cursoId)
      .maybeSingle();

    if (error || !data) return null;
    return {
      cursoId: data.curso_id,
      estado: data.estado,
      iniciadoAt: data.iniciado_at,
      completadoAt: data.completado_at,
      bloquesVistos: (data.avance_bloques ?? []).map(
        (a: { bloque_id: string }) => a.bloque_id,
      ),
    };
  }

  async listarIntentos(cursoId: string): Promise<Intento[]> {
    const { data, error } = await db()
      .from('intentos')
      .select('id, curso_id, nota, correctas, total, aprobado, terminado_at')
      .eq('curso_id', cursoId)
      .order('terminado_at', { ascending: false });

    if (error) return [];
    return (data ?? []).map((i) => ({
      id: i.id,
      cursoId: i.curso_id,
      nota: i.nota,
      correctas: i.correctas,
      total: i.total,
      aprobado: i.aprobado,
      terminadoAt: i.terminado_at,
    }));
  }

  async obtenerPuntos(): Promise<number> {
    // El saldo es la SUMA del libro mayor, y la calcula el servidor: replicar la suma acá
    // sería tener dos fuentes para el mismo número.
    const { data, error } = await db().rpc('saldo_puntos');
    if (error) return 0;
    return Number(data ?? 0);
  }

  async listarInsignias(): Promise<string[]> {
    const { data, error } = await db().from('insignias_ganadas').select('codigo');
    if (error) return [];
    return [...new Set((data ?? []).map((i) => i.codigo as string))];
  }

  async solicitarCanje(premioId: string): Promise<boolean> {
    // La funcion valida el saldo del lado del servidor, descontando lo ya comprometido en
    // solicitudes abiertas: sin eso, alguien con 100 puntos pedia cuatro premios de 100 y
    // le dejaba el problema al supervisor.
    const { error } = await db().rpc('solicitar_canje', { p_premio_id: premioId });
    if (error) throw new Error(error.message);
    return true;
  }

  async listarPremios(espacioId: string): Promise<Premio[]> {
    const { data, error } = await db()
      .from('premios')
      .select('id, espacio_id, icono, nombre, detalle, puntos, activo, orden')
      .eq('espacio_id', espacioId)
      .eq('activo', true)
      .order('orden');

    if (error) return [];
    return (data ?? []).map((p) => ({
      id: p.id,
      espacioId: p.espacio_id,
      icono: p.icono,
      nombre: p.nombre,
      detalle: p.detalle,
      puntos: p.puntos,
      activo: p.activo,
      orden: p.orden,
    }));
  }

  async listarResumen(espacioId: string): Promise<ResumenCurso[]> {
    const cursos = await this.cargarCursos(espacioId);

    // Una consulta para TODAS las inscripciones y otra para TODOS los intentos, en vez de
    // una por curso: con 20 cursos la diferencia son 2 viajes contra 40.
    const [{ data: inscripciones }, { data: intentos }] = await Promise.all([
      supabase
        .from('inscripciones')
        .select('id, curso_id, estado, iniciado_at, completado_at, avance_bloques ( bloque_id )'),
      db().from('intentos').select('curso_id, nota, aprobado'),
    ]);

    const porCurso = new Map(
      (inscripciones ?? []).map((i) => [i.curso_id, i] as const),
    );

    return cursos.map((curso): ResumenCurso => {
      const fila = porCurso.get(curso.id);
      const susIntentos = (intentos ?? []).filter((t) => t.curso_id === curso.id);
      const vistos: string[] = (fila?.avance_bloques ?? []).map(
        (a: { bloque_id: string }) => a.bloque_id,
      );
      const totalBloques = curso.lecciones.reduce((n, l) => n + l.bloques.length, 0);

      return {
        curso,
        inscripcion: fila
          ? {
              cursoId: fila.curso_id,
              estado: fila.estado,
              iniciadoAt: fila.iniciado_at,
              completadoAt: fila.completado_at,
              bloquesVistos: vistos,
            }
          : null,
        mejorNota: susIntentos.length ? Math.max(...susIntentos.map((t) => t.nota)) : null,
        aprobado: susIntentos.some((t) => t.aprobado),
        porcentajeAvance:
          totalBloques === 0 ? 0 : Math.round((100 * vistos.length) / totalBloques),
      };
    });
  }

  async marcarBloque(_cursoId: string, bloqueId: string): Promise<void> {
    // La función resuelve sola el curso y crea la inscripción si hace falta, así que no
    // hay forma de registrar avance sobre un curso al que no se tiene acceso.
    const { error } = await db().rpc('marcar_bloque', { p_bloque_id: bloqueId });
    if (error) throw new Error(`No se pudo guardar tu avance: ${error.message}`);
  }

  /**
   * Se pregunta por la opcion YA elegida, nunca por la lista de correctas: `es_correcta` sigue
   * sin viajar al navegador. Ver `academia.revisar_respuesta` (0176).
   */
  async revisarRespuesta(opcionId: string): Promise<RevisionRespuesta> {
    const { data, error } = await db().rpc('revisar_respuesta', { p_opcion_id: opcionId });
    if (error) throw new Error(`No se pudo revisar tu respuesta: ${error.message}`);
    const r = (data ?? {}) as { correcta?: boolean; opcion_correcta_id?: string | null };
    return {
      correcta: Boolean(r.correcta),
      opcionCorrectaId: r.opcion_correcta_id ?? null,
    };
  }

  async calificarIntento(
    cursoId: string,
    respuestas: RespuestaElegida[],
  ): Promise<ResultadoIntento> {
    const { data, error } = await db().rpc('calificar_intento', {
      p_curso_id: cursoId,
      p_respuestas: respuestas.map((r) => ({
        pregunta_id: r.preguntaId,
        opcion_id: r.opcionId,
      })),
    });

    if (error) throw new Error(`No se pudo calificar tu quiz: ${error.message}`);

    const r = data as Record<string, unknown>;
    return {
      nota: Number(r.nota ?? 0),
      correctas: Number(r.correctas ?? 0),
      total: Number(r.total ?? 0),
      aprobado: Boolean(r.aprobado),
      puntosGanados: Number(r.puntos_ganados ?? 0),
      yaAprobadoAntes: Boolean(r.ya_aprobado_antes),
    };
  }
}
